import * as React from 'react'

import { Button, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import type EmployeePicture from 'modules/employees/store/models/EmployeePicture'

interface Props {
  employeePicture: EmployeePicture
  canRemovePicture: boolean
  canDownloadPicture: boolean
}

const Actions = ({ employeePicture, canRemovePicture, canDownloadPicture }: Props) => {
  const [isOpen, toggleOpen] = useToggle(false)
  const [isPopoverOpen, togglePopover] = useToggle(false)

  const options = React.useMemo<Array<Record<string, any>>>(() => {
    const options = []

    if (canDownloadPicture) {
      options.push({
        href: employeePicture.downloadUrl,
        title: i18n.t('employee_form.face_recognition_info.download'),
      })
    }

    if (canRemovePicture) {
      options.push({
        onClick: toggleOpen(true),
        title: i18n.t('employee_form.face_recognition_info.delete'),
        type: 'wrong',
      })
    }

    return options
  }, [toggleOpen, employeePicture, canDownloadPicture, canRemovePicture])

  const { running: deleting, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      await asyncAction(() => employeePicture.destroy(), {
        successFlash: false,
      })
      closePortal()
    }
  )

  return (
    <>
      {options.length > 0 && (
        <Popover
          attachment="top right"
          open={isPopoverOpen}
          targetAttachment="bottom center"
          toggle={togglePopover}
        >
          <Button onClick={togglePopover(true)} type="secondary" iconName="ellipsis" />
          <PopoverContent onClose={togglePopover(false)} options={options} />
        </Popover>
      )}

      {isOpen && (
        <Modal
          title={i18n.t('employee_form.face_recognition_info.delete_modal.title')}
          description={i18n.t('employee_form.face_recognition_info.delete_modal.description')}
          illustration="input-field"
          onClose={toggleOpen(false)}
          onSubmit={onSubmit}
          footer={
            <Button type="negative" disabled={deleting} submit>
              {i18n.t('employee_form.face_recognition_info.delete_modal.delete')}
            </Button>
          }
        />
      )}
    </>
  )
}

export default observer(Actions)
